// Static privacy policy (ticket #25). Deliberately Convex-free: the page
// must render for signed-out visitors and store reviewers without a
// backend round-trip, and the store listings link to this URL.
//
// Copy names the site through CANONICAL_WEB_HOST (the #204 copy law,
// enforced for web by webOriginCopy.test.ts), never a second literal.
const WHO_WE_ARE_BODY = `ToDoTogether is a shared to-do list app operated as a small independent project. This policy covers the web app at ${CANONICAL_WEB_HOST} and the ToDoTogether mobile apps.`;
import {
  CANONICAL_WEB_HOST,
  CANONICAL_WEB_ORIGIN,
  splitOnWebOrigin,
} from "@todotogether/shared";

/**
 * A body paragraph with the site named in it (ticket #202): mentions of
 * the host render as a real link to the canonical origin instead of dead
 * text. The shared splitter (#204's carrier) owns the matching —
 * lookalike domains stay plain — and the visible host text comes from
 * its web-origin segments.
 */
function Paragraph({ text }: { text: string }) {
  return (
    <p className="mt-2 text-muted">
      {splitOnWebOrigin(text).map((segment, i) =>
        segment.kind === "web-origin" ? (
          <a
            key={i}
            href={CANONICAL_WEB_ORIGIN}
            className="underline hover:text-ink"
          >
            {segment.text}
          </a>
        ) : (
          <span key={i}>{segment.text}</span>
        ),
      )}
    </p>
  );
}
const SECTIONS: { heading: string; body: string[] }[] = [
  {
    heading: "Who we are",
    body: [
      WHO_WE_ARE_BODY,
      "Draft notice: this page is a working draft prepared for store review. The final legal copy is pending review and will replace this text.",
    ],
  },
  {
    heading: "Where your data lives",
    body: [
      "Your account and list data are stored on Convex's European Union cloud (region eu-west-1). We do not store your data outside the EU.",
      "The web app's static files are delivered through Cloudflare's network; that delivery layer holds no personal data.",
    ],
  },
  {
    heading: "What we collect",
    body: [
      "Account: your email address, display name, and chosen accent color. If you sign in with Google or Apple, we receive the identity your provider returns (email and name).",
      "Your content: the lists, items, notes, and reminders you and the people you share lists with create.",
      "Push notifications: a device push token so we can deliver notifications you are entitled to (assignments, due-today reminders).",
      "Purchases: if you subscribe, the subscription status is processed by RevenueCat on our behalf (with Apple, Google, or Stripe as the payment processor). We never see your full payment details.",
      "Usage: we keep it minimal — no advertising identifiers, no cross-app tracking, no sale of data to third parties, ever.",
    ],
  },
  {
    heading: "Sharing with processors",
    body: [
      "We share the minimum necessary data with a short list of processors strictly to run the service: Convex (database, EU), RevenueCat (subscription management), Expo and Firebase Cloud Messaging (push delivery), Stripe (web payments), and Apple or Google (sign-in and in-app purchases). None of them may sell or reuse your data; none receive it for advertising.",
    ],
  },
  {
    heading: "Deleting your account",
    body: [
      "You can delete your account from inside the app (Settings → Account → Delete account) or on the web. Deleting removes your account immediately: lists you own are permanently deleted, your memberships elsewhere are removed, and your name no longer appears to other members.",
    ],
  },
  {
    heading: "Your rights",
    body: [
      "You may request a copy of your data, correction of it, or its deletion. Because account data is EU-hosted, GDPR applies regardless of where you are. Contact us via the support page and we will respond promptly.",
    ],
  },
  {
    heading: "Changes",
    body: [
      "If this policy changes materially we will announce it in the app before the change takes effect.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <main className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="text-3xl font-bold tracking-tight text-ink">
        Privacy policy
      </h1>
      <p className="mt-2 text-sm text-muted">
        Draft for store review (ticket #25) · final legal copy pending.
      </p>
      <div className="mt-8 space-y-8">
        {SECTIONS.map((s, i) => (
          <section key={i}>
            <h2 className="text-lg font-semibold text-ink">{s.heading}</h2>
            {s.body.map((p, j) => (
              <Paragraph key={j} text={p} />
            ))}
          </section>
        ))}
      </div>
    </main>
  );
}
